import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useApp } from "@/contexts/app-context";
import { createEntity, Entity, updateEntity } from "@/services/supabase/entities";
import { getTemplateWithFields, TemplateField } from "@/services/supabase/templates";
import { ToolCallMessagePartComponent, useThreadRuntime } from "@assistant-ui/react";
import { Check, ChevronDown, ChevronUp, History, Loader2, Play, User } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface EntityToolOutput {
    type?: string;
    name: string;
    entityType?: string;
    templateId?: number;
    categoryId?: number;
    fieldValues?: Record<string, any>;
    description?: string;
    id?: number;
    _action?: 'create' | 'update';
}

const tryParseJSON = (jsonString: string) => {
    try {
        return JSON.parse(jsonString);
    } catch (e) {
        return null;
    }
};

// Renders an editable control for a single template field
function FieldInput({
    field,
    value,
    onChange,
}: {
    field: TemplateField;
    value: any;
    onChange: (val: any) => void;
}) {
    const options: string[] = field.options
        ? Array.isArray(field.options)
            ? field.options
            : (field.options as string).split(",").map((o) => o.trim())
        : [];

    switch (field.field_type) {
        case "textarea":
            return (
                <Textarea
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={`Enter ${field.field_name}...`}
                    className="min-h-[80px] bg-background/50 text-sm leading-relaxed resize-none"
                />
            );
        case "number":
            return (
                <Input
                    type="number"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.valueAsNumber || e.target.value)}
                    placeholder={`Enter ${field.field_name}...`}
                    className="bg-background/50"
                />
            );
        case "dropdown":
            return (
                <Select value={value ?? ""} onValueChange={onChange}>
                    <SelectTrigger className="bg-background/50 h-9">
                        <SelectValue placeholder={`Select ${field.field_name}...`} />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map((opt) => (
                            <SelectItem key={opt} value={opt}>
                                {opt}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            );
        case "checkbox":
            return (
                <div className="flex items-center gap-2 pt-1">
                    <Checkbox
                        checked={!!value}
                        onCheckedChange={(checked) => onChange(!!checked)}
                        id={`field-${field.id}`}
                    />
                    <Label htmlFor={`field-${field.id}`} className="text-sm font-normal cursor-pointer">
                        {value ? "Yes" : "No"}
                    </Label>
                </div>
            );
        default:
            return (
                <Input
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={`Enter ${field.field_name}...`}
                    className="bg-background/50"
                />
            );
    }
}

/** Uniform field row: label on top (uppercase tracking), input/value below — consistent with entity details page */
function FieldRow({
    label,
    required,
    children,
}: {
    label: string;
    required?: boolean;
    children: React.ReactNode;
}) {
    return (
        <div className="flex flex-col gap-1.5 px-4 py-3 border-b border-border/60 last:border-0 hover:bg-muted/5 transition-colors">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
                {label}
                {required && <span className="text-destructive ml-0.5">*</span>}
            </span>
            <div>{children}</div>
        </div>
    );
}

export const ManageEntityTool: ToolCallMessagePartComponent = ({
    argsText,
    result,
}) => {
    const threadRuntime = useThreadRuntime();
    const { projectId } = useApp();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [templateFields, setTemplateFields] = useState<TemplateField[]>([]);
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const [editableItems, setEditableItems] = useState<EntityToolOutput[]>([]);
    const [hasUserEdited, setHasUserEdited] = useState(false);

    const rawData = typeof result === "string" ? tryParseJSON(result) : result;
    const isLoading = result === undefined;
    const isHitl = rawData?.hitl === true;

    useEffect(() => {
        if (hasUserEdited) return;
        let items: EntityToolOutput[] = [];
        if (rawData) {
            const payloadData = rawData.data || rawData;
            if (Array.isArray(payloadData)) {
                items = payloadData;
            } else if (payloadData.items && Array.isArray(payloadData.items)) {
                items = payloadData.items;
            } else {
                items = [payloadData];
            }
            setEditableItems(items);
        }
    }, [rawData, hasUserEdited]);

    useEffect(() => {
        const templateId = editableItems[0]?.templateId;
        if (!templateId) return;

        setIsLoadingTemplate(true);
        getTemplateWithFields(templateId)
            .then((tmpl) => setTemplateFields(tmpl.fields || []))
            .catch((err) => console.error("Failed to fetch template fields:", err))
            .finally(() => setIsLoadingTemplate(false));
    }, [editableItems[0]?.templateId]);

    const hasItems = editableItems.length > 0;
    const isUpdate = editableItems.some((item) => item._action === "update");

    const handleAction = async () => {
        if (!projectId) {
            toast.error("Project context not found.");
            return;
        }
        setIsProcessing(true);
        try {
            const promises = editableItems.map(async (item) => {
                if (item._action === "update") {
                    if (!item.id) throw new Error("Missing ID for update");
                    const updateData: Partial<Entity> = {
                        name: item.name,
                        description: item.description,
                    };
                    if (item.fieldValues) {
                        updateData.field_values = item.fieldValues;
                    }
                    return updateEntity(item.id, updateData);
                } else {
                    return createEntity(projectId, {
                        name: item.name,
                        description: item.description,
                        template_id: item.templateId || 1,
                        category_id: item.categoryId || 1,
                        field_values: item.fieldValues || {},
                    });
                }
            });

            await Promise.all(promises);
            toast.success(isUpdate ? "Entities updated!" : "Entities created!");
            setIsCollapsed(true);
        } catch (error) {
            console.error("EntityAction: Error:", error);
            toast.error("Failed to process entities.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUpdateField = (itemIndex: number, fieldName: string, val: any) => {
        setHasUserEdited(true);
        setEditableItems((prev) => {
            const next = [...prev];
            next[itemIndex] = {
                ...next[itemIndex],
                fieldValues: {
                    ...(next[itemIndex].fieldValues || {}),
                    [fieldName]: val,
                },
            };
            return next;
        });
    };

    const handleUpdateItem = (index: number, field: keyof EntityToolOutput, value: any) => {
        setHasUserEdited(true);
        setEditableItems((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center gap-2.5 my-3 px-1 text-sm text-muted-foreground">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                    <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
                </div>
                <span>Generating entity…</span>
            </div>
        );
    }

    const activeItem = editableItems[activeIndex];

    return (
        <div className="my-3 rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
            {/* Header */}
            <div
                className="flex items-center gap-3 px-4 py-3.5 cursor-pointer select-none bg-muted/40 hover:bg-muted/60 transition-colors border-b border-border"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-xl",
                    isUpdate ? "bg-amber-100 dark:bg-amber-900/30 text-amber-600" : "bg-primary/10 text-primary"
                )}>
                    {isUpdate ? <History className="h-4 w-4" /> : <User className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-foreground">
                            {isUpdate
                                ? `Update ${editableItems.length} Entit${editableItems.length > 1 ? "ies" : "y"}`
                                : `Create ${editableItems.length} Entit${editableItems.length > 1 ? "ies" : "y"}`}
                        </p>
                        {editableItems.length > 0 && (
                            <Badge variant="secondary" className="text-[10px] h-4 px-1.5 rounded-sm font-medium">
                                {editableItems.length}
                            </Badge>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {isUpdate ? "Review changes before applying" : "Review and add to project"}
                    </p>
                </div>
                <div className="text-muted-foreground/60">
                    {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                </div>
            </div>

            {!isCollapsed && (
                <div>
                    {hasItems ? (
                        <>
                            {/* Tab strip — shown when multiple entities */}
                            {editableItems.length > 1 && (
                                <div className="flex overflow-x-auto border-b border-border bg-muted/20">
                                    {editableItems.map((item, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setActiveIndex(i)}
                                            className={cn(
                                                "flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors",
                                                i === activeIndex
                                                    ? "border-primary text-foreground"
                                                    : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30"
                                            )}
                                        >
                                            <User className="h-3 w-3" />
                                            {(typeof item.name === 'string' ? item.name : `Entity ${i + 1}`)?.slice(0, 24)}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {activeItem && (
                                <div>
                                    {/* Entity name + action badge */}
                                    <div className="px-4 pt-4 pb-3">
                                        <div className="flex items-center justify-between mb-1.5">
                                            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
                                                Name <span className="text-destructive">*</span>
                                            </label>
                                            <div className="flex items-center gap-1.5">
                                                {activeItem.entityType && (
                                                    <Badge variant="outline" className="text-[10px] h-4 px-1.5 font-normal text-muted-foreground">
                                                        {activeItem.entityType}
                                                    </Badge>
                                                )}
                                                <Badge
                                                    variant={activeItem._action === "update" ? "secondary" : "default"}
                                                    className="text-[10px] h-4 px-1.5 uppercase tracking-wide"
                                                >
                                                    {activeItem._action || "CREATE"}
                                                </Badge>
                                            </div>
                                        </div>
                                        <Input
                                            value={activeItem.name ?? ""}
                                            onChange={(e) => handleUpdateItem(activeIndex, "name", e.target.value)}
                                            className="font-medium bg-background border-border/60 focus-visible:ring-primary/30"
                                            placeholder="Enter entity name…"
                                        />
                                    </div>

                                    <div className="mx-4 border-t border-border/50" />

                                    {/* Description */}
                                    <div className="px-4 pt-3 pb-3">
                                        <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 block mb-1.5">Description</label>
                                        <Textarea
                                            value={activeItem.description ?? ""}
                                            onChange={(e) => handleUpdateItem(activeIndex, "description", e.target.value)}
                                            className="min-h-[70px] leading-relaxed bg-background border-border/60 text-sm resize-none focus-visible:ring-primary/30"
                                            placeholder="Enter entity description…"
                                        />
                                    </div>

                                    {/* Template fields */}
                                    {isLoadingTemplate ? (
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground px-4 py-3">
                                            <Loader2 className="h-3 w-3 animate-spin" />
                                            Loading template fields…
                                        </div>
                                    ) : templateFields.length > 0 ? (
                                        <>
                                            <div className="mx-4 border-t border-border/50" />
                                            <div className="px-4 py-2">
                                                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">Attributes</span>
                                            </div>
                                            <div className="flex flex-col divide-y divide-border/40">
                                                {templateFields.map((field) => (
                                                    <FieldRow key={field.id} label={field.field_name} required={field.is_required}>
                                                        <FieldInput
                                                            field={field}
                                                            value={activeItem.fieldValues?.[field.field_name]}
                                                            onChange={(val) => handleUpdateField(activeIndex, field.field_name, val)}
                                                        />
                                                    </FieldRow>
                                                ))}
                                            </div>
                                        </>
                                    ) : activeItem.fieldValues && Object.keys(activeItem.fieldValues).length > 0 ? (
                                        <>
                                            <div className="mx-4 border-t border-border/50" />
                                            <div className="px-4 py-2">
                                                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">Attributes</span>
                                            </div>
                                            <div className="flex flex-col divide-y divide-border/40">
                                                {Object.entries(activeItem.fieldValues).map(([key, val]) => (
                                                    <FieldRow key={key} label={key.replace(/_/g, " ")}>
                                                        <Input
                                                            value={String(val ?? "")}
                                                            onChange={(e) => handleUpdateField(activeIndex, key, e.target.value)}
                                                            className="bg-background border-border/60"
                                                        />
                                                    </FieldRow>
                                                ))}
                                            </div>
                                        </>
                                    ) : null}

                                    {/* Update badge */}
                                    {activeItem._action === "update" && (
                                        <div className="px-4 py-3">
                                            <div className="inline-flex items-center gap-1.5 text-[10px] text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 rounded-lg border border-amber-200/60 dark:border-amber-800/30">
                                                <History className="h-3 w-3" />
                                                Updating existing entity #{activeItem.id}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Footer */}
                            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
                                <p className="text-[11px] text-muted-foreground/60">{editableItems.length} entit{editableItems.length > 1 ? 'ies' : 'y'} ready</p>
                                <div className="flex gap-2">
                                    <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-foreground" onClick={() => setIsCollapsed(true)}>
                                        Dismiss
                                    </Button>
                                    {isHitl ? (
                                        <Button
                                            size="sm"
                                            className="h-8"
                                            onClick={() => {
                                                const tId = editableItems[0]?.templateId || 1;
                                                threadRuntime.append({
                                                    role: "user",
                                                    content: [{ type: "text", text: `Please continue creating the entity using template ID ${tId}.` }],
                                                });
                                            }}
                                        >
                                            <Play className="mr-1.5 h-3.5 w-3.5 fill-current" />
                                            Continue with AI
                                        </Button>
                                    ) : (
                                        <Button
                                            size="sm"
                                            className={cn("h-8", isUpdate ? "bg-amber-600 hover:bg-amber-700 text-white" : "")}
                                            onClick={handleAction}
                                            disabled={isProcessing}
                                        >
                                            {isProcessing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : isUpdate ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Play className="mr-1.5 h-3.5 w-3.5 fill-current" />}
                                            {isUpdate ? "Apply Changes" : "Create Entities"}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="px-4 py-6 text-center text-muted-foreground text-sm">
                            <p>No valid entity data found.</p>
                            <pre className="mt-2 text-xs text-left bg-muted p-2 rounded-lg overflow-auto max-h-32 opacity-50">{argsText}</pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
